import { epicEditorView, epicOverviewView } from "./view.ts";
import type { Epic, Todo } from "../../domain/entities.ts";
import { escapeHtml } from "../../ui/text.ts";
import { effectiveEpicId } from "./model.ts";

interface Preferences { grouped: Record<string, boolean>; filters: Record<string, string>; collapsed: Record<string, boolean> }
export interface EpicCapabilities {
  rootId(): string | null;
  collectionId(): string | null;
  canEdit(): boolean;
  epics(): Epic[];
  tasks(): Todo[];
  getMeta(key: string): Promise<unknown>;
  setMeta(key: string, value: unknown): Promise<void>;
  save(id: string, rootId: string, update: Partial<Pick<Epic, "name" | "description" | "color" | "archivedAt">>): Promise<unknown>;
  render(): Promise<void>;
  sync(): void;
  openTask(id: string): Promise<void>;
  showTasks(epicId: string): Promise<void>;
  capture(collectionId: string, column: string, epicId: string | null): Promise<void>;
  assignTask(id: string, epicId: string | null): Promise<void>;
  toast(message: string, error?: boolean): void;
}
export function createEpicController(c: EpicCapabilities) {
  let preferences: Preferences = { grouped: {}, filters: {}, collapsed: {} };
  let loaded = false;
  let revealedEpicId: string | undefined;
  const key = () => `${c.rootId() || ""}:${c.collectionId() || "all"}`;
  const persist = () => c.setMeta("epicPreferences", preferences);
  const fail = (error: unknown) => c.toast(error instanceof Error ? error.message : "Could not update goal", true);
  const run = (work: () => Promise<unknown>) => { void work().catch(fail); };
  const available = () => c.epics().filter(epic => epic.rootId === c.rootId() && !epic.deleted);

  function editor(existing?: Epic, onCreated?: (id: string) => void) {
    const rootId = existing?.rootId || c.rootId();
    if (!rootId || !c.canEdit()) return;
    const dialog = document.createElement("dialog");
    dialog.className = "name-dialog epic-dialog";
    dialog.setAttribute("aria-label", existing ? "Edit goal" : "Create goal");
    dialog.innerHTML = epicEditorView(existing);
    document.body.append(dialog);
    dialog.addEventListener("close", () => dialog.remove(), { once: true });
    dialog.querySelector("[data-close]")!.addEventListener("click", () => dialog.close());
    dialog.querySelector("[data-cancel]")!.addEventListener("click", () => dialog.close());
    dialog.querySelector("form")!.addEventListener("submit", event => {
      event.preventDefault();
      const form = event.currentTarget as HTMLFormElement;
      const data = new FormData(form), name = String(data.get("name") || "").trim();
      if (!name) return;
      const submit = form.querySelector<HTMLButtonElement>("[type=submit]")!;
      submit.disabled = true;
      run(async () => {
        try {
          const id = existing?.id || crypto.randomUUID();
          await c.save(id, rootId, { name, description: String(data.get("description") || ""), color: String(data.get("color") || "indigo"), archivedAt: existing?.archivedAt || null });
          dialog.close();
          if (!existing) revealedEpicId = id;
          onCreated?.(id);
          if (!onCreated) await c.render(); c.sync();
        } catch (error) { const message = form.querySelector<HTMLElement>("[data-error]")!; message.hidden = false; message.textContent = error instanceof Error ? error.message : String(error); submit.disabled = false; }
      });
    });
    dialog.showModal();
  }

  function overview(selectedId?: string) {
    const selected = available().find(epic => epic.id === selectedId);
    const dialog = document.createElement("dialog");
    dialog.className = "name-dialog epic-dialog";
    dialog.setAttribute("aria-label", selected ? selected.name : "Goals");
    dialog.innerHTML = epicOverviewView(available(), c.tasks(), c.canEdit(), selected);
    document.body.append(dialog);
    dialog.addEventListener("close", () => dialog.remove(), { once: true });
    dialog.querySelector("[data-close]")!.addEventListener("click", () => dialog.close());
    dialog.querySelector("[data-new]")?.addEventListener("click", () => { dialog.close(); editor(); });
    dialog.querySelectorAll<HTMLElement>("[data-open]").forEach(button => button.addEventListener("click", () => { dialog.close(); overview(button.dataset.open); }));
    dialog.querySelector("[data-edit]")?.addEventListener("click", () => { dialog.close(); editor(selected); });
    dialog.querySelector("[data-show]")?.addEventListener("click", () => run(async () => { dialog.close(); await c.showTasks(selected!.id); }));
    dialog.querySelector("[data-archive]")?.addEventListener("click", () => run(async () => { await c.save(selected!.id, selected!.rootId, { archivedAt: selected!.archivedAt ? null : Date.now() }); dialog.close(); await c.render(); c.sync(); }));
    dialog.querySelectorAll<HTMLElement>("[data-task]").forEach(button => button.addEventListener("click", () => run(async () => { dialog.close(); await c.openTask(button.dataset.task!); })));
    dialog.showModal();
  }

  function bind(root: Document) {
    const toolbar = root.querySelector(".task-toolbar, .calendar-toolbar");
    if (toolbar && !available().length && c.rootId() && !root.querySelector("[data-epics]")) {
      const menu = toolbar.querySelector(".task-toolbar-more > [role=menu], .calendar-options > div");
      if (menu) {
        const button = document.createElement("button");
        button.type = "button"; button.dataset.epics = ""; button.setAttribute("role", "menuitem"); button.textContent = "Goals";
        menu.append(button);
      }
    }
    if (toolbar && available().length && !root.querySelector(".epic-controls") && c.rootId()) {
      const control = document.createElement("div");
      control.className = "epic-controls";
      control.innerHTML = `<button type="button" data-epics>Goals</button><label>Group by<select data-epic-group><option value="none">None</option><option value="epic" ${preferences.grouped[key()] ? "selected" : ""}>Goal</option></select></label><label>Goal<select data-epic-filter><option value="">All goals</option><option value="none" ${filter() === "none" ? "selected" : ""}>No goal</option>${available().filter(epic => !epic.archivedAt || epic.id === filter()).map(epic => `<option value="${epic.id}" ${filter() === epic.id ? "selected" : ""}>${escapeHtml(epic.name)}</option>`).join("")}</select></label>`;
      if (toolbar.matches(".calendar-toolbar")) control.querySelector("[data-epic-group]")?.closest("label")?.remove();
      toolbar.insertAdjacentElement("afterend", control);
      control.querySelector<HTMLSelectElement>("[data-epic-group]")?.addEventListener("change", event => run(async () => { preferences.grouped[key()] = (event.target as HTMLSelectElement).value === "epic"; await persist(); await c.render(); }));
      control.querySelector<HTMLSelectElement>("[data-epic-filter]")!.addEventListener("change", event => run(async () => { preferences.filters[key()] = (event.target as HTMLSelectElement).value; await persist(); await c.render(); }));
    }
    root.querySelectorAll<HTMLElement>("[data-epics]").forEach(button => button.addEventListener("click", () => {
      const menu = button.closest("details");
      if (menu) { menu.open = false; menu.querySelector("summary")?.focus(); }
      overview();
    }));
    root.querySelectorAll<HTMLElement>("[data-epic-overview]").forEach(button => button.addEventListener("click", () => overview()));
    root.querySelectorAll<HTMLElement>("[data-epic-open]").forEach(button => button.addEventListener("click", () => overview(button.dataset.epicOpen)));
    root.querySelectorAll<HTMLInputElement>("[data-epic-search]").forEach(input => input.addEventListener("input", () => { const select = input.closest("label")?.querySelector("select"); if (select) Array.from(select.options).forEach(option => { option.hidden = !!option.value && !option.textContent?.toLocaleLowerCase().includes(input.value.toLocaleLowerCase()); }); }));
    root.querySelectorAll<HTMLElement>("[data-epic-create]").forEach(button => button.addEventListener("click", () => {
      const form = button.closest("form");
      editor(undefined, id => {
        const select = form?.querySelector<HTMLSelectElement>("[name=epicId]");
        if (select) { select.add(new Option(c.epics().find(epic => epic.id === id)?.name || "New goal", id)); select.value = id; select.dispatchEvent(new Event("change", { bubbles: true })); }
      });
    }));
    root.querySelectorAll<HTMLElement>("[data-epic-capture]").forEach(button => button.addEventListener("click", () => run(async () => { await c.capture(button.dataset.collectionId!, button.dataset.column!, button.dataset.epicCapture || null); })));
    root.querySelectorAll<HTMLDetailsElement>("[data-epic-lane]").forEach(lane => {
      const laneKey = `${key()}:${lane.dataset.epicLane}`;
      lane.open = !preferences.collapsed[laneKey];
      lane.addEventListener("toggle", () => { preferences.collapsed[laneKey] = !lane.open; run(persist); });
    });
    // A keyboard-accessible assignment control on every board card.
    if (c.canEdit() && available().length) root.querySelectorAll<HTMLElement>(".kanban-card").forEach(card => {
      const task = c.tasks().find(task => task.id === card.dataset.todo);
      if (!task) return;
      const label = document.createElement("label"); label.className = "epic-card-move";
      label.innerHTML = `<span>Move to goal</span><select aria-label="Move ${escapeHtml(task.title)} to goal"><option value="">No goal</option>${available().filter(epic => !epic.archivedAt || epic.id === task.epicId).map(epic => `<option value="${epic.id}" ${epic.id === task.epicId ? "selected" : ""}>${escapeHtml(epic.name)}</option>`).join("")}</select>`;
      label.querySelector("select")!.addEventListener("change", event => run(async () => { await c.assignTask(task.id, (event.target as HTMLSelectElement).value || null); }));
      const disclosure = document.createElement("details"); disclosure.className = "epic-card-actions"; disclosure.innerHTML = "<summary>Move to goal</summary>"; disclosure.append(label); card.append(disclosure);
    });
  }
  function filter() { return preferences.filters[key()] || ""; }
  return { bind, filter, revealed: () => revealedEpicId, grouped: () => !!preferences.grouped[key()], async load() { if (loaded) return; const value = await c.getMeta("epicPreferences") as Partial<Preferences> | undefined; preferences = { grouped: value?.grouped || {}, filters: value?.filters || {}, collapsed: value?.collapsed || {} }; loaded = true; } };
}
